import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import {
  calculateStudentAttendanceSummary,
} from "./AttendanceRecordService";

const HOURS_PER_WEEK = 20;
const WEEK_NUMBERS = Array.from(
  { length: 10 },
  (_, index) => index + 1
);

function safeFilename(value) {
  return String(value || "attendance")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function clean(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function getWeek(student, weekNumber) {
  return (
    student?.weeks?.[`week${weekNumber}`] || {
      attendanceHours: "",
      leaveApplied: false,
      leaveHours: "",
    }
  );
}

/**
 * Converts the application record into the Excel notation:
 *
 * 20     -> normal attendance hours
 * L      -> full approved leave, credited as 20 hours
 * L-8    -> approved leave for 8 credited hours
 */
function getExcelWeekValue(student, weekNumber) {
  const week = getWeek(student, weekNumber);

  if (week.leaveApplied) {
    const hasSpecificLeaveHours =
      week.leaveHours !== "" &&
      week.leaveHours != null &&
      Number.isFinite(Number(week.leaveHours));

    if (!hasSpecificLeaveHours) {
      return "L";
    }

    return `L-${Number(week.leaveHours)}`;
  }

  const hasAttendance =
    week.attendanceHours !== "" &&
    week.attendanceHours != null &&
    Number.isFinite(Number(week.attendanceHours));

  return hasAttendance
    ? Number(week.attendanceHours)
    : "";
}

function getExcelColumnLetter(columnNumber) {
  let result = "";
  let number = columnNumber;

  while (number > 0) {
    const remainder = (number - 1) % 26;

    result =
      String.fromCharCode(65 + remainder) +
      result;

    number = Math.floor((number - 1) / 26);
  }

  return result;
}

function buildCreditedCellExpression(cellReference) {
  return [
    `IF(${cellReference}="",0,`,
    `IF(LEFT(${cellReference},1)="L",`,
    `IF(${cellReference}="L",${HOURS_PER_WEEK},`,
    `IFERROR(VALUE(MID(${cellReference},3,LEN(${cellReference}))),0)),`,
    `IFERROR(VALUE(${cellReference}),0)))`,
  ].join("");
}

function buildCreditedHoursFormula(rowNumber) {
  const weekCellReferences = WEEK_NUMBERS.map(
    (_, index) => {
      // Week 1 begins in column G.
      const columnNumber = 7 + index;

      return `${getExcelColumnLetter(
        columnNumber
      )}${rowNumber}`;
    }
  );

  return weekCellReferences
    .map(buildCreditedCellExpression)
    .join("+");
}

function buildAttendancePercentageFormula(rowNumber) {
  const start = `G${rowNumber}`;
  const end = `P${rowNumber}`;
  const creditedHoursCell = `Q${rowNumber}`;

  return [
    "IFERROR(",
    "ROUND(",
    `${creditedHoursCell}/`,
    `(COUNTIF(${start}:${end},"<>")*${HOURS_PER_WEEK})`,
    "*100,0)",
    ",0)",
  ].join("");
}

function applyThinBorder(cell) {
  cell.border = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };
}

function formatDateForDisplay(value) {
  if (!value) {
    return "";
  }

  const [year, month, day] =
    String(value).split("-");

  if (!year || !month || !day) {
    return String(value);
  }

  return `${day}/${month}/${year}`;
}

function courseIdentity(course = {}) {
  return [
    clean(course.courseCode),
    clean(course.courseName),
  ].join("::");
}

function sanitiseWorksheetName(value) {
  const cleaned = String(value || "Course")
    .replace(/[\\/*?:\[\]]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return (cleaned || "Course").slice(0, 31);
}

function getUniqueWorksheetName(
  courseName,
  usedWorksheetNames
) {
  const baseName = sanitiseWorksheetName(
    courseName
  );

  let sheetName = baseName;
  let sequence = 2;

  while (
    usedWorksheetNames.has(
      sheetName.toLowerCase()
    )
  ) {
    const suffix = ` (${sequence})`;
    const maximumBaseLength =
      31 - suffix.length;

    sheetName = `${baseName.slice(
      0,
      maximumBaseLength
    )}${suffix}`;

    sequence += 1;
  }

  usedWorksheetNames.add(
    sheetName.toLowerCase()
  );

  return sheetName;
}

function normaliseCourses(courses = []) {
  const uniqueCourses = new Map();

  courses.forEach((course) => {
    const courseName = String(
      course?.courseName || ""
    ).trim();

    const students = Array.isArray(
      course?.students
    )
      ? course.students.filter(
          (student) =>
            String(
              student?.studentId || ""
            ).trim()
        )
      : [];

    if (!courseName || students.length === 0) {
      return;
    }

    const key = courseIdentity(course);

    uniqueCourses.set(key, {
      courseCode: String(
        course.courseCode || ""
      ).trim(),

      courseName,
      students,
    });
  });

  return [...uniqueCourses.values()].sort(
    (a, b) =>
      a.courseName.localeCompare(
        b.courseName
      )
  );
}

function addCourseWorksheet({
  workbook,
  usedWorksheetNames,
  campus,
  year,
  term,
  termStartDate,
  termEndDate,
  courseCode,
  courseName,
  students,
}) {
  const worksheetName =
    getUniqueWorksheetName(
      courseName,
      usedWorksheetNames
    );

  const worksheet = workbook.addWorksheet(
    worksheetName,
    {
      views: [
        {
          state: "frozen",
          xSplit: 6,
          ySplit: 5,
        },
      ],

      pageSetup: {
        orientation: "landscape",
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,

        margins: {
          left: 0.25,
          right: 0.25,
          top: 0.4,
          bottom: 0.4,
          header: 0.15,
          footer: 0.15,
        },
      },
    }
  );

  const title =
    `Student Attendance ${term}, ${year} - ${campus}`;

  const courseDisplay = courseCode
    ? `${courseCode} - ${courseName}`
    : courseName;

  worksheet.mergeCells("A1:R1");
  worksheet.getCell("A1").value = title;
  worksheet.getCell("A1").font = {
    bold: true,
    size: 16,
  };
  worksheet.getCell("A1").alignment = {
    horizontal: "center",
    vertical: "middle",
  };
  worksheet.getRow(1).height = 26;

  worksheet.mergeCells("A2:R2");
  worksheet.getCell("A2").value =
    `Course: ${courseDisplay}`;
  worksheet.getCell("A2").font = {
    bold: true,
    size: 11,
  };
  worksheet.getCell("A2").alignment = {
    horizontal: "left",
    vertical: "middle",
  };

  worksheet.mergeCells("A3:I3");
  worksheet.getCell("A3").value =
    `Term Start: ${
      formatDateForDisplay(termStartDate) ||
      "—"
    }`;

  worksheet.mergeCells("J3:R3");
  worksheet.getCell("J3").value =
    `Term End: ${
      formatDateForDisplay(termEndDate) ||
      "—"
    }`;

  ["A3", "J3"].forEach((reference) => {
    const cell = worksheet.getCell(reference);

    cell.font = {
      bold: true,
    };

    cell.alignment = {
      horizontal: "left",
      vertical: "middle",
    };
  });

  worksheet.getRow(4).height = 7;

  const headings = [
    "No.",
    "Student ID",
    "Student Name",
    "Enrolment Type",
    "Contact Number",
    "Email Address",

    ...WEEK_NUMBERS.map(
      (weekNumber) => `Week ${weekNumber}`
    ),

    "Total Credited Hours",
    "Attendance %",
  ];

  worksheet.getRow(5).values = headings;
  worksheet.getRow(5).height = 30;

  worksheet.getRow(5).eachCell((cell) => {
    cell.font = {
      bold: true,
    };

    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };

    applyThinBorder(cell);
  });

  const sortedStudents = [...students].sort(
    (a, b) =>
      String(
        a.studentName || ""
      ).localeCompare(
        String(b.studentName || "")
      )
  );

  sortedStudents.forEach(
    (student, index) => {
      const rowNumber = 6 + index;

      const summary =
        calculateStudentAttendanceSummary(
          student
        );

      const row = worksheet.getRow(rowNumber);

      row.values = [
        index + 1,
        student.studentId || "",
        student.studentName || "",
        student.enrolmentType || "",
        student.contactNumber || "",
        student.contactEmail || "",

        ...WEEK_NUMBERS.map((weekNumber) =>
          getExcelWeekValue(
            student,
            weekNumber
          )
        ),
      ];

      row.height = 22;

      worksheet.getCell(
        `Q${rowNumber}`
      ).value = {
        formula:
          buildCreditedHoursFormula(
            rowNumber
          ),

        result:
          summary.totalCreditedHours,
      };

      worksheet.getCell(
        `R${rowNumber}`
      ).value = {
        formula:
          buildAttendancePercentageFormula(
            rowNumber
          ),

        result:
          summary.attendancePercentage,
      };

      worksheet.getCell(
        `R${rowNumber}`
      ).numFmt = '0"%"';

      row.eachCell(
        { includeEmpty: true },
        (cell, columnNumber) => {
          applyThinBorder(cell);

          cell.alignment = {
            horizontal:
              [3, 5, 6].includes(
                columnNumber
              )
                ? "left"
                : "center",

            vertical: "middle",
            wrapText: true,
          };
        }
      );
    }
  );

  worksheet.columns = [
    { key: "number", width: 7 },
    { key: "studentId", width: 16 },
    { key: "studentName", width: 28 },
    { key: "enrolmentType", width: 15 },
    { key: "contactNumber", width: 18 },
    { key: "contactEmail", width: 30 },

    ...WEEK_NUMBERS.map(() => ({
      width: 11,
    })),

    {
      key: "creditedHours",
      width: 22,
    },

    {
      key: "attendancePercentage",
      width: 16,
    },
  ];

  const lastRowNumber =
    5 + sortedStudents.length;

  worksheet.autoFilter = {
    from: "A5",
    to: `R${lastRowNumber}`,
  };

  worksheet.headerFooter.oddFooter =
    `&L${courseDisplay}&C${title}&RPage &P of &N`;
}

/**
 * Creates one workbook for the selected Campus + Year + Term.
 * Every course is placed on a separate worksheet.
 *
 * No fill colours or font colours are defined.
 */
export async function downloadAllCoursesAttendanceExcel({
  campus,
  year,
  term,
  termStartDate,
  termEndDate,
  courses,
}) {
  if (!campus) {
    throw new Error("Campus is required.");
  }

  if (!year) {
    throw new Error("Year is required.");
  }

  if (!term) {
    throw new Error("Term is required.");
  }

  const normalisedCourses =
    normaliseCourses(courses);

  if (normalisedCourses.length === 0) {
    throw new Error(
      "At least one course with students is required."
    );
  }

  const workbook = new ExcelJS.Workbook();

  workbook.creator =
    "Canberra Institute of Education and Technology";

  workbook.created = new Date();
  workbook.modified = new Date();

  const usedWorksheetNames = new Set();

  normalisedCourses.forEach((course) => {
    addCourseWorksheet({
      workbook,
      usedWorksheetNames,
      campus,
      year,
      term,
      termStartDate,
      termEndDate,
      courseCode: course.courseCode,
      courseName: course.courseName,
      students: course.students,
    });
  });

  const title =
    `Student Attendance ${term}, ${year} - ${campus}`;

  const buffer =
    await workbook.xlsx.writeBuffer();

  const filename = safeFilename(
    `${title}.xlsx`
  );

  saveAs(
    new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
}

/**
 * Backward-compatible single-course export.
 */
export async function downloadStudentAttendanceExcel({
  campus,
  courseCode,
  courseName,
  year,
  term,
  termStartDate,
  termEndDate,
  students,
}) {
  return downloadAllCoursesAttendanceExcel({
    campus,
    year,
    term,
    termStartDate,
    termEndDate,

    courses: [
      {
        courseCode,
        courseName,
        students,
      },
    ],
  });
}